/**
 * Procurement Demo — Nami answers for the questions that belong to one page.
 *
 * THROWAWAY DEMO CODE. Five pages used to have their own "Ask" box or tab: the
 * contracts list, one agreement, the bid page, a supplier and a material. They
 * now offer their questions as chips in Nami. Each one is answered in the chat with the same
 * text as before, followed by a "Sources" line of links that open the agreement
 * clause, the bid section or the supplier tab behind it. The answers are only
 * offered on their own page, so a supplier's questions never show on another
 * supplier. `suggestions.ts` shows the chips and the interceptor plays the
 * answers.
 */

import { matchPhrasing } from '../ask/match-scripted';
import type { AskCitation, ScriptedAnswer } from '../ask/types';
import { isContractId } from '../data/contracts';
import { isMaterialId } from '../data/materials';
import { getSupplier, isSupplierId } from '../data/suppliers';
import {
  repositorySuggestions,
  suggestionsFor,
} from '../pages/contracts/contracts-qa';
import { BID_SUGGESTIONS } from '../pages/sourcing/sourcing-ask';
import { BID_DOCUMENTS } from '../pages/sourcing/sourcing-bid-documents';
import { RFP_ID } from '../pages/sourcing/sourcing-data';
import { buildMaterialSuggestions } from '../pages/suppliers/materials-ask';
import { buildSupplierColumns } from '../pages/suppliers/materials-model';
import {
  PROFILE_DOCUMENT_ID,
  buildSuggestions,
} from '../pages/suppliers/supplier360-ask';
import { SUPPLIER_TABS } from '../pages/suppliers/supplier360-tabs';
import type { SupplierTab } from '../pages/suppliers/supplier360-tabs';
import type { SupplierId } from '../data/types';
import { matchProcurementDemoScript } from './script';
import { contractSourceLink, sourcesLine } from './source-links';
import type { SourceLink } from './source-links';

export interface PageAnswer {
  /** The question, as shown on the chip. */
  question: string;
  /** Phrasings that start this answer: the question plus any other wording. */
  phrasings: string[];
  /** Markdown: the answer, then a "Sources" line of links. */
  narration: string;
}

type LinkFor = (citation: AskCitation) => SourceLink | null;

function toPageAnswer(scripted: ScriptedAnswer, linkFor: LinkFor): PageAnswer {
  return {
    question: scripted.question,
    phrasings: [scripted.question, ...(scripted.aliases ?? [])],
    narration: `${scripted.answer}${sourcesLine(scripted.citations.map(linkFor))}`,
  };
}

/** The bid page, with the cited section of the bid or bid request highlighted. */
function bidSourceLink(citation: AskCitation): SourceLink | null {
  const document = BID_DOCUMENTS.find(
    (item) => item.id === citation.documentId,
  );
  if (!document) return null;
  return {
    label: `${document.shortTitle} §${citation.ref}`,
    href: `/procurement/sourcing/${RFP_ID}?tab=documents&source=${document.id}:${citation.ref}`,
  };
}

/** A fact from the supplier profile is shown on one of the supplier's tabs. */
const PROFILE_REF_TAB: Record<string, SupplierTab> = {
  'P.1': 'overview',
  'P.2': 'performance',
  'P.3': 'risk',
  'P.4': 'profile',
  'P.5': 'overview',
};

function supplierSourceLinkFor(supplierId: SupplierId): LinkFor {
  const supplier = getSupplier(supplierId);
  return (citation) => {
    if (citation.documentId !== PROFILE_DOCUMENT_ID) {
      return contractSourceLink(citation);
    }
    const tab = PROFILE_REF_TAB[citation.ref] ?? 'overview';
    const tabLabel =
      SUPPLIER_TABS.find((item) => item.value === tab)?.label ?? tab;
    return {
      label: `${supplier.shortName} ${tabLabel.toLowerCase()}`,
      href: `/procurement/suppliers/${supplierId}?tab=${tab}`,
    };
  };
}

function segmentsOf(pathname: string): string[] {
  return pathname.split('/').filter((segment) => segment.length > 0);
}

/** An agreement's own page: its questions have no older prompt that opens a page. */
function isAgreementPage(pathname: string): boolean {
  const [area, section, id] = segmentsOf(pathname);
  return area === 'procurement' && section === 'contracts' && id !== undefined;
}

/** The answers offered on this page, or an empty list when it has none. */
function buildPageAnswers(pathname: string): PageAnswer[] {
  const [area, section, id, ...rest] = segmentsOf(pathname);
  if (area !== 'procurement' || rest.length > 0) return [];

  if (section === 'contracts' && id === undefined) {
    return repositorySuggestions().map((answer) =>
      toPageAnswer(answer, contractSourceLink),
    );
  }
  if (section === 'contracts' && id !== undefined && isContractId(id)) {
    // An agreement's own questions: each source highlights its clause in the
    // document on the page.
    return suggestionsFor(id).map((answer) =>
      toPageAnswer(answer, contractSourceLink),
    );
  }
  if (section === 'sourcing' && id === RFP_ID) {
    return BID_SUGGESTIONS.map((answer) => toPageAnswer(answer, bidSourceLink));
  }
  if (section === 'suppliers' && id !== undefined && isSupplierId(id)) {
    const linkFor = supplierSourceLinkFor(id);
    return buildSuggestions(getSupplier(id)).map((answer) =>
      toPageAnswer(answer, linkFor),
    );
  }
  if (section === 'materials' && id !== undefined && isMaterialId(id)) {
    return buildMaterialSuggestions(id, buildSupplierColumns(id)).map(
      (answer) => toPageAnswer(answer, contractSourceLink),
    );
  }
  return [];
}

const cache = new Map<string, PageAnswer[]>();

/** The answers for a page. Built once per address, so the chips keep their identity. */
export function pageAnswersForPath(pathname: string): PageAnswer[] {
  const cached = cache.get(pathname);
  if (cached) return cached;
  const built = buildPageAnswers(pathname);
  cache.set(pathname, built);
  return built;
}

/**
 * The answer for a user message on this page, or undefined when it is not one.
 *
 * A chip's own wording always gets its answer. Other wording is matched loosely,
 * unless an older Nami prompt already handles it (and then opens a page). On an
 * agreement's page every match answers in place: the page is already open.
 */
export function matchPageAnswer(
  message: string,
  pathname: string,
): PageAnswer | undefined {
  const answers = pageAnswersForPath(pathname);
  if (answers.length === 0) return undefined;
  const answer = matchPhrasing(
    message,
    answers,
    (candidate) => candidate.phrasings,
  );
  if (!answer) return undefined;
  const isChipWording = answers.some(
    (candidate) => candidate.question === message,
  );
  if (isChipWording || isAgreementPage(pathname)) return answer;
  return matchProcurementDemoScript(message) === null ? answer : undefined;
}
